/**
 * Breadcrumbs - Navigation breadcrumb component
 * Accessible implementation with proper ARIA attributes
 */

import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  maxItems?: number;
  className?: string;
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("w-4 h-4", className)}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("w-4 h-4", className)}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

export function Breadcrumbs({
  items,
  separator,
  maxItems = 4,
  className,
}: BreadcrumbsProps) {
  // Collapse items if exceeding maxItems
  let displayItems = items;
  let hasCollapsed = false;

  if (items.length > maxItems) {
    // Keep first item, collapsed indicator, and last (maxItems - 2) items
    const firstItem = items[0];
    const lastItems = items.slice(-(maxItems - 2));
    displayItems = [
      firstItem,
      { label: "...", href: undefined },
      ...lastItems,
    ];
    hasCollapsed = true;
  }

  const defaultSeparator = (
    <ChevronRightIcon className="text-gray-400 flex-shrink-0" />
  );

  return (
    <nav aria-label="Breadcrumb" className={cn("flex", className)}>
      <ol className="flex items-center gap-2">
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;
          const isCollapsed = hasCollapsed && index === 1;

          return (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && (separator || defaultSeparator)}

              {isCollapsed ? (
                <span
                  className="text-gray-400 px-1"
                  aria-hidden="true"
                  title="Collapsed items"
                >
                  {item.label}
                </span>
              ) : isLast ? (
                <span
                  className="text-gray-900 dark:text-gray-100 font-medium truncate max-w-[200px]"
                  aria-current="page"
                  title={item.label}
                >
                  {item.icon && (
                    <span className="inline-flex mr-1.5">{item.icon}</span>
                  )}
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.href || "#"}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex items-center truncate max-w-[200px]"
                  title={item.label}
                >
                  {item.icon && (
                    <span className="inline-flex mr-1.5">{item.icon}</span>
                  )}
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Predefined breadcrumb builders for common pages
 */
export const breadcrumbBuilders = {
  dashboard: (): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
  ],

  leads: (): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Leady" },
  ],

  leadDetail: (leadId: string, clientName?: string): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Leady", href: "/leads" },
    { label: clientName || `Lead #${leadId.slice(0, 8)}` },
  ],

  sessions: (): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Sesje" },
  ],

  sessionDetail: (
    sessionId: string,
    clientName?: string
  ): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Sesje", href: "/sessions" },
    { label: clientName || `Sesja #${sessionId.slice(0, 8)}` },
  ],

  templates: (): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Szablony" },
  ],

  profile: (): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Profil" },
  ],

  publicProfile: (): BreadcrumbItem[] => [
    { label: "Dashboard", href: "/dashboard", icon: <HomeIcon /> },
    { label: "Profil", href: "/profile" },
    { label: "Profil publiczny" },
  ],

  // Admin routes
  adminDashboard: (): BreadcrumbItem[] => [
    { label: "Admin", href: "/admin", icon: <HomeIcon /> },
  ],

  adminAdvisors: (): BreadcrumbItem[] => [
    { label: "Admin", href: "/admin", icon: <HomeIcon /> },
    { label: "Doradcy" },
  ],

  adminAuditLogs: (): BreadcrumbItem[] => [
    { label: "Admin", href: "/admin", icon: <HomeIcon /> },
    { label: "Logi aktywności" },
  ],
};

export type { BreadcrumbItem, BreadcrumbsProps };
